interface SalesData {
  date: Date;
  value: number;
}

export interface HistoricalDataPoint {
  year: number;
  value: number;
  month?: number;
  quarter?: number;
}

export interface ProjectionData {
  projectedValue: number;
  historicalData: HistoricalDataPoint[];
  averageHistorical: number;
  variationFromAverage: number;
  confidence: 'high' | 'medium' | 'low';
}

export class ProjectionService {
  static calculateMonthlyProjection(
    allSalesData: SalesData[],
    targetMonth: number,
    targetYear: number
  ): ProjectionData | null {
    const historicalData: HistoricalDataPoint[] = [];

    allSalesData.forEach(item => {
      const itemMonth = item.date.getMonth();
      const itemYear = item.date.getFullYear();

      if (itemMonth === targetMonth && itemYear < targetYear) {
        const existingYear = historicalData.find(h => h.year === itemYear);
        if (existingYear) {
          existingYear.value += item.value;
        } else {
          historicalData.push({
            year: itemYear,
            value: item.value,
            month: targetMonth
          });
        }
      }
    });

    if (historicalData.length === 0) return null;

    historicalData.sort((a, b) => b.year - a.year);

    const recentYears = historicalData.slice(0, 5);

    let projectedValue: number;
    if (recentYears.length >= 3) {
      const weights = [0.4, 0.3, 0.2, 0.1, 0.1];
      projectedValue = recentYears.reduce((sum, data, index) => {
        return sum + data.value * (weights[index] || 0);
      }, 0);
    } else {
      projectedValue = recentYears.reduce((sum, data) => sum + data.value, 0) / recentYears.length;
    }

    const averageHistorical = historicalData.reduce((sum, data) => sum + data.value, 0) / historicalData.length;
    const variationFromAverage = ((projectedValue - averageHistorical) / averageHistorical) * 100;

    let confidence: 'high' | 'medium' | 'low';
    if (historicalData.length >= 5) {
      confidence = 'high';
    } else if (historicalData.length >= 3) {
      confidence = 'medium';
    } else {
      confidence = 'low';
    }

    return {
      projectedValue,
      historicalData,
      averageHistorical,
      variationFromAverage,
      confidence
    };
  }

  static calculateQuarterlyProjection(
    allSalesData: SalesData[],
    targetQuarter: number,
    targetYear: number
  ): ProjectionData | null {
    const historicalData: HistoricalDataPoint[] = [];

    allSalesData.forEach(item => {
      const itemMonth = item.date.getMonth();
      const itemQuarter = Math.floor(itemMonth / 3) + 1;
      const itemYear = item.date.getFullYear();

      if (itemQuarter === targetQuarter && itemYear < targetYear) {
        const existingYear = historicalData.find(h => h.year === itemYear);
        if (existingYear) {
          existingYear.value += item.value;
        } else {
          historicalData.push({
            year: itemYear,
            value: item.value,
            quarter: targetQuarter
          });
        }
      }
    });

    if (historicalData.length === 0) return null;

    historicalData.sort((a, b) => b.year - a.year);

    const recentYears = historicalData.slice(0, 5);

    let projectedValue: number;
    if (recentYears.length >= 3) {
      const weights = [0.4, 0.3, 0.2, 0.1, 0.1];
      projectedValue = recentYears.reduce((sum, data, index) => {
        return sum + data.value * (weights[index] || 0);
      }, 0);
    } else {
      projectedValue = recentYears.reduce((sum, data) => sum + data.value, 0) / recentYears.length;
    }

    const averageHistorical = historicalData.reduce((sum, data) => sum + data.value, 0) / historicalData.length;
    const variationFromAverage = ((projectedValue - averageHistorical) / averageHistorical) * 100;

    let confidence: 'high' | 'medium' | 'low';
    if (historicalData.length >= 5) {
      confidence = 'high';
    } else if (historicalData.length >= 3) {
      confidence = 'medium';
    } else {
      confidence = 'low';
    }

    return {
      projectedValue,
      historicalData,
      averageHistorical,
      variationFromAverage,
      confidence
    };
  }

  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  static getConfidenceLabel(confidence: 'high' | 'medium' | 'low'): string {
    const labels = {
      high: 'Alta confiança (5+ anos de histórico)',
      medium: 'Média confiança (3-4 anos de histórico)',
      low: 'Baixa confiança (menos de 3 anos de histórico)'
    };
    return labels[confidence];
  }

  static getConfidenceColor(confidence: 'high' | 'medium' | 'low'): string {
    const colors = {
      high: 'text-green-600',
      medium: 'text-yellow-600',
      low: 'text-orange-600'
    };
    return colors[confidence];
  }
}
